/**
 * Shared helper for surfacing the client-editable Main Job "Other" cost rows
 * (group2–group5) in the Estimating and Sites work-details accordions.
 *
 * Background: Cost Settings lets the client define custom "Other" line items
 * for service_trades / flooring / internal_demolition / preliminary_cost.
 * Those rows are persisted in the shared cost_parameters table and surface in
 * `GET /sites/default-work-items` (and saved site/quote workItems) with a
 * category-scoped prefixed sectionId, e.g. `other_flooring_placeholder`.
 *
 * The existing Estimating/Sites merge logic only UPDATES pre-seeded accordion
 * items by exact id, so dynamic custom rows were silently dropped. This util
 * fully (re)builds the "Other" accordion item for each in-scope category from
 * the cost-param rows — mirroring how group6's "Other" is handled in
 * quoteJobAdditionalUtils.ts. Cost Settings is the source of truth for the
 * row set (labels + default rates); per-row estimator state (inScope, qty,
 * notes, totalCost) is preserved across a rebuild when the row already exists.
 *
 * Structural types are used (not the per-file AccordionItem interfaces, which
 * are duplicated across EstimatingCreate/EstimatingEdit/SiteDetailsTab) so the
 * single implementation works with all three call sites.
 */
import { MAIN_OTHER_CATEGORIES, OTHER_ACCORDION_ITEM_ID, getMainOtherSectionPrefix, } from "../cost-parameters/costParametersDefaults";
const PRESERVED_KEYS = [
    "inScope",
    "qty",
    "weeksQty",
    "numberQty",
    "notes",
    "totalCost",
    "unitCost",
    "selectedOption",
];
/**
 * Rebuild the "Other" accordion item for every in-scope Main Job category
 * from the given cost-param rows. Mutates `data` in place.
 *
 * - Cost Settings owns the row set: items not present in `rows` are removed,
 *   new ones are added, labels/defaultUnitCost come from `rows`.
 * - Estimator state for a surviving row (same itemId) is carried over so a
 *   prices refresh / reload doesn't wipe scoping or quantities.
 * - The "Other" accordion item is created on demand; the large per-file
 *   getDefaultAccordionData() literals are intentionally not touched.
 */
export function applyMainOtherFromCostParams(data, rows) {
    if (!rows || rows.length === 0)
        return;
    for (const category of MAIN_OTHER_CATEGORIES) {
        const prefix = getMainOtherSectionPrefix(category);
        const categoryRows = rows.filter((r) => r.category === category && r.sectionId.startsWith(prefix));
        if (categoryRows.length === 0)
            continue;
        const categoryItems = data[category];
        if (!categoryItems)
            continue;
        // Snapshot existing Other sub-items by id so estimator state survives.
        const existingById = new Map();
        const existingOther = categoryItems.find((i) => i.id === OTHER_ACCORDION_ITEM_ID);
        if (existingOther?.sections) {
            for (const section of existingOther.sections) {
                for (const sub of section.items)
                    existingById.set(sub.id, sub);
            }
        }
        // Group incoming rows by their (full, prefixed) sectionId, preserving
        // backend order (rows arrive sorted by SortOrder).
        const sectionOrder = [];
        const rowsBySection = new Map();
        for (const row of categoryRows) {
            let bucket = rowsBySection.get(row.sectionId);
            if (!bucket) {
                bucket = [];
                rowsBySection.set(row.sectionId, bucket);
                sectionOrder.push(row.sectionId);
            }
            bucket.push(row);
        }
        const sections = sectionOrder.map((sectionId) => {
            const sectionRows = rowsBySection.get(sectionId) ?? [];
            return {
                sectionId,
                sectionTitle: sectionRows[0]?.sectionTitle || "Other",
                items: sectionRows.map((row) => {
                    const sub = {
                        id: row.itemId,
                        title: row.itemTitle,
                        inScope: false,
                        unitCost: row.unitCost != null ? `${row.unitCost}` : "",
                        totalCost: "",
                        notes: "",
                        defaultUnitCost: row.defaultUnitCost ?? row.unitCost ?? null,
                    };
                    const prev = existingById.get(row.itemId);
                    if (prev) {
                        for (const key of PRESERVED_KEYS) {
                            const value = prev[key];
                            if (value !== undefined) {
                                sub[key] = value;
                            }
                        }
                    }
                    return sub;
                }),
            };
        });
        const otherItem = {
            id: OTHER_ACCORDION_ITEM_ID,
            title: "Other",
            sections,
        };
        if (existingOther) {
            existingOther.title = "Other";
            existingOther.sections = sections;
        }
        else {
            categoryItems.push(otherItem);
        }
    }
}
